/**
 * 系统配置管理（管理员）。
 *
 * 系统配置列表（免费额度类型/数量、USDT 地址等）+ 编辑保存。
 *
 * @module pages/admin/ConfigAdminPage
 */
import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import EditIcon from '@mui/icons-material/Edit';

import PageHeader from '@/components/common/PageHeader';
import DataTable, { type Column } from '@/components/common/DataTable';
import { useToast } from '@/hooks/useToast';
import { useApi } from '@/hooks/useApi';
import { listConfigs, putConfig } from '@/services/admin';
import type { SystemConfig } from '@/types';

/**
 * 系统配置管理页组件。
 *
 * @returns 页面元素。
 */
export default function ConfigAdminPage(): JSX.Element {
  const { data, loading, refetch } = useApi(() => listConfigs());
  const { show, Toast } = useToast();

  const [editKey, setEditKey] = useState<string | null>(null);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const current: SystemConfig | undefined = (data ?? []).find(
    (c) => c.configKey === editKey,
  );

  const openEdit = (c: SystemConfig): void => {
    setEditKey(c.configKey);
    setValue(c.configValue);
  };

  const handleSave = async (): Promise<void> => {
    if (!editKey) return;
    setSaving(true);
    try {
      await putConfig(editKey, { configValue: value });
      show('配置已保存');
      setEditKey(null);
      await refetch();
    } catch (err) {
      show(err instanceof Error ? err.message : '保存失败', 'error');
    } finally {
      setSaving(false);
    }
  };

  const columns: Column<SystemConfig>[] = [
    { key: 'configKey', header: '配置键' },
    {
      key: 'description',
      header: '说明',
      render: (c) => c.description || '-',
    },
    { key: 'configType', header: '类型', render: (c) => c.configType },
    { key: 'configValue', header: '配置值', render: (c) => <Typography sx={{ wordBreak: 'break-all' }}>{c.configValue}</Typography> },
    {
      key: 'actions',
      header: '操作',
      align: 'right',
      render: (c) => (
        <Button size="small" startIcon={<EditIcon />} onClick={() => openEdit(c)}>
          编辑
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader title="系统配置" subtitle="管理免费额度、USDT 收款地址等系统参数" />

      <DataTable
        columns={columns}
        rows={data ?? []}
        rowKey={(c) => c.configKey}
        loading={loading}
        emptyText="暂无系统配置"
      />

      <Dialog open={editKey != null} onClose={() => !saving && setEditKey(null)} maxWidth="sm" fullWidth>
        <DialogTitle>编辑配置：{editKey}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {current?.description || '—'}
          </Typography>
          <TextField
            label="配置值"
            fullWidth
            multiline
            minRows={2}
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditKey(null)} disabled={saving}>
            取消
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>
      {Toast}
    </Box>
  );
}
